import { AcUnitOutlined, AttachMoneyOutlined, Close, Wallet } from "@mui/icons-material"
import { Box, Button, Card, CardContent, Dialog, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { EezycoinWallet, getWalletsByOwnerId, setupPersonalWalletForOwner } from "src/apis/eezycoin"
import { Id } from "src/utils/types"
import Bold from "../typography/Bold"
import NoDataAlert from "../NoDataAlert"
import { isUserAdministrator } from "src/utils/auth"
import EezycoinWalletAdjustmentDialog from "./EezycoinWalletAdjustmentDialog"

type EezycoinWalletDialogProps = {
  ownerId: Id,
  ownerType: string,
  setError: (err: any) => void
}

export default function EezycoinWalletDialog({ownerId, ownerType, setError}: EezycoinWalletDialogProps) {
  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [wallets, setWallets] = useState<EezycoinWallet[]>([])

  const isAdmin = useMemo(isUserAdministrator, [])

  const onClose = (): void => setOpen(false)

  const onOpen = (): void => setOpen(true)

  const fetchData = async (): Promise<void> => {
    if (!isAdmin) return;

    setLoading(true)

    try {
      const wallets = await getWalletsByOwnerId(ownerId)

      setWallets(wallets)
      setError(null)
    } catch(err: any) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin) {
    return <></>
  }

  return <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          Eezycoin - Carteiras
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      {wallets?.length === 0 ? <>
        <DialogContent>
          {loading ? (
            <Box width="100%" sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
              <NoDataAlert message="Carregando carteiras..." />
            </Box>
          ) : (
            <Box width="100%" sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
              <NoDataAlert message="Não encontramos carteiras cadastradas" />

              <Box>
                <Button variant="contained" size="medium" onClick={async () => {
                  if (window.confirm("Deseja mesmo iniciar uma carteira para esse cliente?")) {
                    const wallet = await setupPersonalWalletForOwner(ownerId, ownerType)

                    setWallets([wallet])
                  }
                }}>
                  Iniciar carteira
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </> : <>
        <DialogContent dividers>
          {wallets?.map((w, i) => (
            <Card variant="outlined" sx={{marginBottom: '1em'}}>
              <CardContent>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {w?.type === 'PERSONAL' && (
                      <Bold>Carteira Pessoal</Bold>
                    )}
                  </Box>

                  <Box>
                    <EezycoinWalletAdjustmentDialog
                      walletId={w?.id}
                      walletOriginalBalance={w?.coin_balance}
                      setError={setError}
                    />
                  </Box>
                </Box>

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Bold>Saldo</Bold>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AttachMoneyOutlined fontSize="small" /><Bold fontSize="small">Disponíveis</Bold>
                        </Box>
                      </TableCell>
                      <TableCell>{w?.coin_balance} moedas</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AcUnitOutlined fontSize="small" /><Bold fontSize="small">Congeladas</Bold>
                        </Box>
                      </TableCell>
                      <TableCell>{w?.frozen_coin_balance} moedas</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Wallet fontSize="small" /><Bold fontSize="small">Total</Bold>
                        </Box>
                      </TableCell>
                      <TableCell>{w?.total_coin_balance} moedas</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
      </>}
    </Dialog>

    <Tooltip title="Ver carteiras de Eezycoin">
      <IconButton
        size="small"
        onClick={onOpen}
      >
        <Wallet fontSize="small" />
      </IconButton>
    </Tooltip>
  </>
}
